import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LoadingSpinner, getGoogleRedirectUri } from '../components';
import { PKCE_VERIFIER_KEY, landAfterGoogleSignIn } from '../components/GoogleSignInButton';
import { Capacitor } from '@capacitor/core';
import { authService } from '../services';

/**
 * GoogleCallbackPage — Google redirects here (/auth/google/callback) after the
 * user consents. We validate the CSRF `state`, exchange the `code` for our own
 * tokens via the backend, then hard-redirect into the app so all contexts reload
 * (mirrors LoginPage's window.location.href behaviour).
 */
export default function GoogleCallbackPage() {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const ranRef = useRef(false);

  useEffect(() => {
    // Guard against React 18 StrictMode double-invoke — the code is single-use.
    if (ranRef.current) return;
    ranRef.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const googleError = searchParams.get('error');

    const expectedState = sessionStorage.getItem('googleOAuthState');
    const redirect = sessionStorage.getItem('googleOAuthRedirect') || '/square';
    sessionStorage.removeItem('googleOAuthState');
    sessionStorage.removeItem('googleOAuthRedirect');

    if (googleError) {
      setError('Google sign-in was cancelled.');
      return;
    }
    if (!code) {
      setError('Missing authorization code from Google.');
      return;
    }
    if (!state || !expectedState || state !== expectedState) {
      setError('Sign-in could not be verified. Please try again.');
      return;
    }

    (async () => {
      // One-shot: a verifier that outlived its exchange is just a stale secret.
      const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
      sessionStorage.removeItem(PKCE_VERIFIER_KEY);
      const result = await authService.googleLogin(code, getGoogleRedirectUri(), verifier, Capacitor.getPlatform());
      if (result.success) {
        // Shared with the Android picker, which finishes without ever coming here.
        landAfterGoogleSignIn(result, redirect);
      } else {
        setError(result.error || 'Google sign-in failed. Please try again.');
      }
    })();
  }, [searchParams]);

  // White while it works, black only to carry an error.
  //
  // Google's own account chooser is white and so is the app's first paint, so a dark screen in
  // the middle made the round-trip strobe — white, black, white — on top of the two portal
  // entrances. Waiting quietly on the same colour either side of it is the whole difference.
  return (
    <div className={`min-h-screen flex flex-col items-center justify-center p-4 ${error ? 'bg-black' : 'bg-white'}`}>
      {error ? (
        <div className="w-full max-w-md bg-white p-8 text-center">
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-600">
            <p className="font-mono text-sm text-red-600 uppercase tracking-wider">{error}</p>
          </div>
          <Link
            to="/login"
            className="font-mono text-sm uppercase tracking-wider text-black hover:underline"
          >
            &larr; Back to login
          </Link>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4">
          <LoadingSpinner size="lg" />
          <p className="font-mono text-sm uppercase tracking-wider text-gray-500">
            Signing you in&hellip;
          </p>
        </div>
      )}
    </div>
  );
}
